import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  FormControlLabel,
  Checkbox,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Tooltip,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useLocale } from 'shared/contexts/LocaleContext';
import SlateEditor from './reflections/SlateEditor';

// Shared rich-text field (same editor/toolbar as the Reflect tool) for the
// free-text description/abstract fields across these profile dialogs.
export const RichTextField = ({ label, value, onChange, placeholder, height = 160 }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
      {label}
    </Typography>
    <SlateEditor value={value} onChange={onChange} placeholder={placeholder} height={height} />
  </Box>
);

const toDayjs = (val) => (val ? dayjs(val) : null);
const fromDayjs = (val) => (val && val.isValid() ? val.format('YYYY-MM-DD') : null);

const DATE_FORMAT_MAP = { dmy: 'DD/MM/YYYY', mdy: 'MM/DD/YYYY', ymd: 'YYYY-MM-DD' };

export const ProfileDatePicker = ({ label, value, onChange, disabled = false, disableFuture = false }) => {
  const [open, setOpen] = useState(false);
  const { datePref } = useLocale();
  const format = DATE_FORMAT_MAP[datePref] || 'DD/MM/YYYY';
  return (
    <DatePicker
      label={label}
      value={toDayjs(value)}
      onChange={(v) => onChange(fromDayjs(v))}
      format={format}
      disabled={disabled}
      disableFuture={disableFuture}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      views={['year', 'month', 'day']}
      openTo="day"
      slotProps={{
        textField: {
          fullWidth: true,
          onClick: () => { if (!disabled) setOpen(true); },
          sx: { cursor: 'pointer' },
        },
        popper: {
          placement: 'bottom-start',
          sx: { zIndex: 1700 },
        },
        day: {
          sx: { fontSize: '1.2rem' },
        },
        calendarHeader: { format: 'MMMM YYYY' },
        // actionBar: { actions: ['clear'] },
        desktopPaper: {
          sx: {
            pb: 0,
            '& .MuiPickersLayout-actionBar': { pt: 0, minHeight: 0 },
            '& .MuiDialogActions-root': { pt: 0, pb: 0.5 },
          },
        },
      }}
    />
  );
};

export const ProfileDateTimePicker = ({
  label,
  value,
  onChange,
  disabled = false,
  disablePast = false,
  size = 'medium',
  defaultTime = '00:00',
}) => {
  const [open, setOpen] = useState(false);
  const { datePref } = useLocale();
  const dateFormat = DATE_FORMAT_MAP[datePref] || 'DD/MM/YYYY';

  const dayjsVal = value ? dayjs(value) : null;
  const isValid  = dayjsVal && dayjsVal.isValid();
  const datePart = isValid ? dayjsVal.format('YYYY-MM-DD') : null;
  const timePart = isValid ? dayjsVal.format('HH:mm') : '';

  const handleDateChange = (newDate) => {
    if (!newDate || !newDate.isValid()) { onChange(null); return; }
    onChange(`${newDate.format('YYYY-MM-DD')}T${timePart || defaultTime}`);
  };

  const handleTimeChange = (e) => {
    const t = e.target.value;
    if (datePart && t) onChange(`${datePart}T${t}`);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
      <DatePicker
        label={label}
        value={dayjsVal}
        onChange={handleDateChange}
        format={dateFormat}
        disabled={disabled}
        disablePast={disablePast}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        views={['year', 'month', 'day']}
        openTo="day"
        slotProps={{
          textField: {
            size,
            sx: { flex: 1 },
            onClick: () => { if (!disabled) setOpen(true); },
          },
          popper: { placement: 'bottom-start', sx: { zIndex: 1700 } },
          day: { sx: { fontSize: '1.2rem' } },
          calendarHeader: { format: 'MMMM YYYY' },
          desktopPaper: {
            sx: { '& .MuiPickersLayout-actionBar': { pt: 0, minHeight: 0 } },
          },
        }}
      />
      <TextField
        type="time"
        size={size}
        value={timePart}
        onChange={handleTimeChange}
        disabled={disabled}
        slotProps={{ htmlInput: { step: 300 } }}
        sx={{ width: size === 'small' ? 84 : 100 }}
      />
    </Box>
  );
};

export const EducationDialog = ({ open, onClose, onSave, education = null, loading = false }) => {
  const [formData, setFormData] = useState({
    degree: education?.degree || '',
    field_of_study: education?.field_of_study || '',
    institution: education?.institution || '',
    start_date: education?.start_date || '',
    end_date: education?.end_date || '',
    is_current: education?.is_current || false,
    description: education?.description || '',
  });

  // Reset form when education prop changes
  useEffect(() => {
    setFormData({
      degree: education?.degree || '',
      field_of_study: education?.field_of_study || '',
      institution: education?.institution || '',
      start_date: education?.start_date || '',
      end_date: education?.end_date || '',
      is_current: education?.is_current || false,
      description: education?.description || '',
    });
  }, [education]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const dataToSave = { ...formData };
    if (!dataToSave.start_date) dataToSave.start_date = null;
    if (dataToSave.is_current || !dataToSave.end_date) dataToSave.end_date = null;
    if (education?.id) dataToSave.id = education.id;
    onSave(dataToSave);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: 1600 }}>
      <DialogTitle>{education ? 'Edit Education' : 'Add Education'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Degree"
            value={formData.degree}
            onChange={(e) => handleChange('degree', e.target.value)}
            placeholder="Ph.D., M.S., B.S., etc."
            required
            fullWidth
          />
          <TextField
            label="Field of Study"
            value={formData.field_of_study}
            onChange={(e) => handleChange('field_of_study', e.target.value)}
            placeholder="Computer Science, Biology, etc."
            required
            fullWidth
          />
          <TextField
            label="Institution"
            value={formData.institution}
            onChange={(e) => handleChange('institution', e.target.value)}
            placeholder="University name"
            required
            fullWidth
          />
          <Stack direction="row" spacing={1}>
            <ProfileDatePicker
              label="Start Date"
              value={formData.start_date}
              onChange={(val) => handleChange('start_date', val)}
              disableFuture
            />
            <ProfileDatePicker
              label="End Date"
              value={formData.end_date}
              onChange={(val) => handleChange('end_date', val)}
              disabled={formData.is_current}
              disableFuture
            />
          </Stack>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.is_current}
                onChange={(e) => {
                  handleChange('is_current', e.target.checked);
                  if (e.target.checked) handleChange('end_date', null);
                }}
              />
            }
            label="Currently pursuing"
          />
          <RichTextField
            label="Description (optional)"
            value={formData.description}
            onChange={(val) => handleChange('description', val)}
            placeholder="Awards, honors, thesis topic, etc."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.degree || !formData.field_of_study || !formData.institution}
        >
          {loading ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const WorkExperienceDialog = ({ open, onClose, onSave, experience = null, loading = false }) => {
  const [formData, setFormData] = useState({
    position: experience?.position || '',
    organization: experience?.organization || '',
    location: experience?.location || '',
    start_date: experience?.start_date || '',
    end_date: experience?.end_date || '',
    is_current: experience?.is_current || false,
    description: experience?.description || '',
  });

  // Reset form when experience prop changes
  useEffect(() => {
    setFormData({
      position: experience?.position || '',
      organization: experience?.organization || '',
      location: experience?.location || '',
      start_date: experience?.start_date || '',
      end_date: experience?.end_date || '',
      is_current: experience?.is_current || false,
      description: experience?.description || '',
    });
  }, [experience]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const dataToSave = { ...formData };
    if (!dataToSave.start_date) dataToSave.start_date = null;
    if (dataToSave.is_current || !dataToSave.end_date) dataToSave.end_date = null;
    if (experience?.id) dataToSave.id = experience.id;
    onSave(dataToSave);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: 1600 }}>
      <DialogTitle>{experience ? 'Edit Work Experience' : 'Add Work Experience'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Position/Title"
            value={formData.position}
            onChange={(e) => handleChange('position', e.target.value)}
            placeholder="Senior Research Scientist"
            required
            fullWidth
          />
          <TextField
            label="Organization"
            value={formData.organization}
            onChange={(e) => handleChange('organization', e.target.value)}
            placeholder="Company or institution name"
            required
            fullWidth
          />
          <TextField
            label="Location"
            value={formData.location}
            onChange={(e) => handleChange('location', e.target.value)}
            placeholder="City, State/Country"
            fullWidth
          />
          <Stack direction="row" spacing={1}>
            <ProfileDatePicker
              label="Start Date"
              value={formData.start_date}
              onChange={(val) => handleChange('start_date', val)}
              disableFuture
            />
            <ProfileDatePicker
              label="End Date"
              value={formData.end_date}
              onChange={(val) => handleChange('end_date', val)}
              disabled={formData.is_current}
              disableFuture
            />
          </Stack>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.is_current}
                onChange={(e) => {
                  handleChange('is_current', e.target.checked);
                  if (e.target.checked) handleChange('end_date', null);
                }}
              />
            }
            label="Currently working here"
          />
          <RichTextField
            label="Description (optional)"
            value={formData.description}
            onChange={(val) => handleChange('description', val)}
            placeholder="Responsibilities, achievements, key projects..."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !formData.position || !formData.organization}
        >
          {loading ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const PublicationDialog = ({ open, onClose, onSave, publication = null, loading = false }) => {
  const [formData, setFormData] = useState({
    title: publication?.title || '',
    authors: publication?.authors || '',
    journal: publication?.journal || '',
    year: publication?.year || new Date().getFullYear(),
    volume: publication?.volume || '',
    issue: publication?.issue || '',
    pages: publication?.pages || '',
    doi: publication?.doi || '',
    url: publication?.url || '',
    // abstract: publication?.abstract || '',
    publication_type: publication?.publication_type || 'journal',
    citation_count: publication?.citation_count || 0,
  });

  const [fetchingDoi, setFetchingDoi] = useState(false);
  const [doiError, setDoiError] = useState(null);
  const [doiSuccess, setDoiSuccess] = useState(false);

  // Reset form when dialog opens or publication changes
  useEffect(() => {
    if (open) {
      setFormData({
        title: publication?.title || '',
        authors: publication?.authors || '',
        journal: publication?.journal || '',
        year: publication?.year || new Date().getFullYear(),
        volume: publication?.volume || '',
        issue: publication?.issue || '',
        pages: publication?.pages || '',
        doi: publication?.doi || '',
        url: publication?.url || '',
        // abstract: publication?.abstract || '',
        publication_type: publication?.publication_type || 'journal',
        citation_count: publication?.citation_count || 0,
      });
      setDoiError(null);
      setDoiSuccess(false);
    }
  }, [open, publication]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'doi') {
      setDoiError(null);
      setDoiSuccess(false);
    }
  };

  const handleFetchFromDoi = async () => {
    if (!formData.doi) {
      setDoiError('Please enter a DOI first');
      return;
    }

    setFetchingDoi(true);
    setDoiError(null);
    setDoiSuccess(false);

    try {
      const API_BASE = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api').replace(/\/+$/, '');
      
      const response = await fetch(`${API_BASE}/publications/fetch-from-doi/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ doi: formData.doi }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch publication data');
      }

      const data = await response.json();
      
      // Auto-populate form with fetched data
      setFormData(prev => ({
        ...prev,
        title: data.title || prev.title,
        authors: data.authors || prev.authors,
        journal: data.journal || prev.journal,
        year: data.year || prev.year,
        volume: data.volume || prev.volume,
        issue: data.issue || prev.issue,
        pages: data.pages || prev.pages,
        url: data.url || prev.url,
        abstract: data.abstract || prev.abstract,
        publication_type: data.publication_type || prev.publication_type,
        citation_count: data.citation_count || prev.citation_count,
      }));
      
      setDoiSuccess(true);
    } catch (error) {
      console.error('DOI fetch error:', error);
      setDoiError(error.message || 'Failed to fetch publication data from DOI');
    } finally {
      setFetchingDoi(false);
    }
  };

  const handleSubmit = () => {
    const dataToSave = { ...formData };
    if (publication?.id) dataToSave.id = publication.id;
    onSave(dataToSave);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ zIndex: 1600 }}>
      <DialogTitle>{publication ? 'Edit Publication' : 'Add Publication'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {doiSuccess && (
            <Alert severity="success" onClose={() => setDoiSuccess(false)}>
              Publication data fetched successfully! Review and edit as needed.
            </Alert>
          )}
          {doiError && (
            <Alert severity="error" onClose={() => setDoiError(null)}>
              {doiError}
            </Alert>
          )}
          
          <Alert severity="info">
            <strong>Tip:</strong> Enter a DOI and click the search button to automatically fetch publication details.
          </Alert>
          
          <TextField
            label="DOI"
            value={formData.doi}
            onChange={(e) => handleChange('doi', e.target.value)}
            placeholder="10.1234/example.doi or https://doi.org/10.1234/example.doi"
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Fetch publication data from DOI">
                    <IconButton
                      onClick={handleFetchFromDoi}
                      disabled={fetchingDoi || !formData.doi}
                      color="primary"
                      edge="end"
                    >
                      {fetchingDoi ? <CircularProgress size={20} /> : <SearchIcon />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Title"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Authors"
            value={formData.authors}
            onChange={(e) => handleChange('authors', e.target.value)}
            placeholder="Smith, J., Doe, A., et al."
            required
            fullWidth
          />
          <TextField
            label="Journal/Venue"
            value={formData.journal}
            onChange={(e) => handleChange('journal', e.target.value)}
            placeholder="Nature, Science, etc."
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Year"
              type="number"
              value={formData.year}
              onChange={(e) => handleChange('year', e.target.value)}
              fullWidth
            />
            <TextField
              label="Publication Type"
              select
              value={formData.publication_type}
              onChange={(e) => handleChange('publication_type', e.target.value)}
              fullWidth
            >
              <MenuItem value="journal">Journal Article</MenuItem>
              <MenuItem value="conference">Conference Paper</MenuItem>
              <MenuItem value="book">Book</MenuItem>
              <MenuItem value="chapter">Book Chapter</MenuItem>
              <MenuItem value="preprint">Preprint</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Volume"
              value={formData.volume}
              onChange={(e) => handleChange('volume', e.target.value)}
              fullWidth
            />
            <TextField
              label="Issue"
              value={formData.issue}
              onChange={(e) => handleChange('issue', e.target.value)}
              fullWidth
            />
            <TextField
              label="Pages"
              value={formData.pages}
              onChange={(e) => handleChange('pages', e.target.value)}
              placeholder="123-456"
              fullWidth
            />
          </Stack>
          <TextField
            label="URL"
            value={formData.url}
            onChange={(e) => handleChange('url', e.target.value)}
            placeholder="https://..."
            fullWidth
          />
          <TextField
            label="Citation Count"
            type="number"
            value={formData.citation_count}
            onChange={(e) => handleChange('citation_count', e.target.value)}
            fullWidth
          />
          <RichTextField
            label="Abstract (optional)"
            value={formData.abstract}
            onChange={(val) => handleChange('abstract', val)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !formData.title || !formData.authors}
        >
          {loading ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ProjectDialog = ({ open, onClose, onSave, project = null, loading = false }) => {
  const [formData, setFormData] = useState({
    title: project?.title || '',
    role: project?.role || '',
    organization: project?.organization || '',
    funding_source: project?.funding_source || '',
    funding_amount: project?.funding_amount || '',
    start_date: project?.start_date || '',
    end_date: project?.end_date || '',
    is_current: project?.is_current || false,
    description: project?.description || '',
    url: project?.url || '',
    status: project?.status || 'active',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const dataToSave = { ...formData };
    if (!dataToSave.start_date) dataToSave.start_date = null;
    if (dataToSave.is_current || !dataToSave.end_date) dataToSave.end_date = null;
    if (project?.id) dataToSave.id = project.id;
    onSave(dataToSave);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ zIndex: 1600 }}>
      <DialogTitle>{project ? 'Edit Project' : 'Add Project'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Project Title"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Your Role"
            value={formData.role}
            onChange={(e) => handleChange('role', e.target.value)}
            placeholder="Principal Investigator, Co-Investigator, etc."
            required
            fullWidth
          />
          <TextField
            label="Organization"
            value={formData.organization}
            onChange={(e) => handleChange('organization', e.target.value)}
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Funding Source"
              value={formData.funding_source}
              onChange={(e) => handleChange('funding_source', e.target.value)}
              placeholder="NIH, NSF, etc."
              fullWidth
            />
            <TextField
              label="Funding Amount ($)"
              type="number"
              value={formData.funding_amount}
              onChange={(e) => handleChange('funding_amount', e.target.value)}
              fullWidth
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <ProfileDatePicker
              label="Start Date"
              value={formData.start_date}
              onChange={(val) => handleChange('start_date', val)}
            />
            <ProfileDatePicker
              label="End Date"
              value={formData.end_date}
              onChange={(val) => handleChange('end_date', val)}
              disabled={formData.is_current}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_current}
                  onChange={(e) => {
                    handleChange('is_current', e.target.checked);
                    if (e.target.checked) handleChange('end_date', null);
                  }}
                />
              }
              label="Ongoing project"
            />
            <TextField
              label="Status"
              select
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
              fullWidth
            >
              <MenuItem value="planning">Planning</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="on_hold">On Hold</MenuItem>
            </TextField>
          </Stack>
          <TextField
            label="Project URL"
            value={formData.url}
            onChange={(e) => handleChange('url', e.target.value)}
            placeholder="https://..."
            fullWidth
          />
          <RichTextField
            label="Description (optional)"
            value={formData.description}
            onChange={(val) => handleChange('description', val)}
            placeholder="Project goals, outcomes, key findings..."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.title || !formData.role}
        >
          {loading ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const PatentDialog = ({ open, onClose, onSave, patent = null, loading = false }) => {
  const { datePref } = useLocale();
  const dtFormat = DATE_FORMAT_MAP[datePref] || 'DD/MM/YYYY';

  const [formData, setFormData] = useState({
    title: '',
    inventors: '',
    patent_number: '',
    status: 'granted',
    filing_date: '',
    grant_date: '',
    country: '',
    assignee: '',
    abstract: '',
    url: '',
  });

  useEffect(() => {
    if (open) {
      setFormData({
        title: patent?.title || '',
        inventors: patent?.inventors || '',
        patent_number: patent?.patent_number || '',
        status: patent?.status || 'granted',
        filing_date: patent?.filing_date || '',
        grant_date: patent?.grant_date || '',
        country: patent?.country || '',
        assignee: patent?.assignee || '',
        abstract: patent?.abstract || '',
        url: patent?.url || '',
      });
    }
  }, [open, patent]);

  const set = (field) => (e) => setFormData(p => ({ ...p, [field]: e.target.value }));

  const handleSubmit = () => {
    const data = { ...formData };
    if (!data.filing_date) data.filing_date = null;
    if (!data.grant_date) data.grant_date = null;
    if (patent?.id) data.id = patent.id;
    onSave(data);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ zIndex: 1600 }}>
      <DialogTitle>{patent ? 'Edit Patent' : 'Add Patent'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Patent Title *" value={formData.title} onChange={set('title')} required fullWidth />
          <TextField label="Inventors" value={formData.inventors} onChange={set('inventors')}
            placeholder="Comma-separated list of inventors" fullWidth />
          <Stack direction="row" spacing={2}>
            <TextField label="Patent Number" value={formData.patent_number} onChange={set('patent_number')} fullWidth />
            <TextField select label="Status" value={formData.status} onChange={set('status')} fullWidth>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="granted">Granted</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="abandoned">Abandoned</MenuItem>
            </TextField>
          </Stack>
          <Stack direction="row" spacing={2}>
            <ProfileDatePicker
              label="Filing Date"
              value={formData.filing_date}
              onChange={(v) => setFormData(p => ({ ...p, filing_date: v }))}
            />
            <ProfileDatePicker
              label="Grant Date"
              value={formData.grant_date}
              onChange={(v) => setFormData(p => ({ ...p, grant_date: v }))}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Country / Jurisdiction" value={formData.country} onChange={set('country')} fullWidth />
            <TextField label="Assignee" value={formData.assignee} onChange={set('assignee')}
              placeholder="Organization that owns the patent" fullWidth />
          </Stack>
          <RichTextField label="Abstract" value={formData.abstract}
            onChange={(val) => setFormData(p => ({ ...p, abstract: val }))} />
          <TextField label="URL / Patent Office Link" value={formData.url} onChange={set('url')}
            type="url" placeholder="https://patents.google.com/..." fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || !formData.title}>
          {loading ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
